/*
 * Lista de devoluciones.
 *
 * Lista de devoluciones paginada con filtros de estatus, fecha, ubicación
 * y creador. También se muestra un buscador por palabras general.
 *
 * Created on Wed Apr 12 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Searcher, filters and actions buttons -->
    <RefundsListFilters @on:filter="handleFilter" />
    <!-- #endregion::Searcher, filters and actions buttons -->

    <!-- #region::Skeleton -->
    <template v-if="isLoadingRefunds">
      <b-skeleton
        class="mt-1"
        animation="wave"
        width="100%"
        height="430px"
      />
    </template>
    <!-- #endregion::Skeleton -->

    <template v-else>
      <b-card
        class="mt-1 border border-dark shadow-none"
        no-body
      >
        <b-card-text>

          <!-- #region::Refunds list table -->
          <b-table
            v-if="availableRefunds"
            :items="refunds"
            :fields="columns"
            responsive
            class="my-0"
          >

            <!-- #region:Virtual column for index -->
            <template #cell(No)="data">
              {{ indexFrom + data.index }}
            </template>
            <!-- #endregion:Virtual column for index -->

            <!-- #region:Virtual column for ID -->
            <template #cell(ID)="{ item }">
              <router-link :to="{ name: 'refund-details', params: { id: item.IdDevolution } }">
                {{ item.ID }}
              </router-link>
            </template>
            <!-- #endregion:Virtual column for ID -->

            <!-- #region:Virtual column for order ID -->
            <template #cell(OrderID)="{ item }">
              <span>{{ item.OrderID }}</span>
            </template>
            <!-- #endregion:Virtual column for ID -->

            <!-- #region::Virtual column for subtotal mxn -->
            <template #cell(TotalMxn)="{ item }">
              <span v-if=" item.TotalMxn">
                {{ item.TotalMxn | currency }}
              </span>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for subtotal mxn -->

            <!-- #region::Virtual column for created date -->
            <template #cell(created_at)="{ item }">
              {{ item.created_at | moment("DD/MM/YYYY hh:mm a") }}
            </template>
            <!-- #endregion::Virtual column for created date -->

            <!-- #region::Virtual column for status refund -->
            <template #cell(status)="{ item }">
              <template v-if="item.status">
                <div
                  :class="['sm-banner-alert', statusColorStyle(item.status)]"
                  role="alert"
                  class="text-nowrap"
                  style="display: inline; font-size: 11px;"
                >
                  {{ item.status }}
                </div>
              </template>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for status refund -->

            <!-- #region::Virtual column for received month date -->
            <template #cell(received_month)="{ item }">
              <span
                v-if="item.received_at"
                class="text-nowrap"
              >{{ item.received_at | moment("MMMM YYYY") | capitalize }}</span>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for received month date -->

            <!-- #region::Virtual column for received date -->
            <template #cell(received_date)="{ item }">
              <span
                v-if="item.received_at"
                class="text-nowrap"
              >{{ item.received_at | moment("DD/MM/YYYY") }}</span>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for received date -->

            <!-- #region::Virtual column for refund payment -->
            <template #cell(RefundPayment)="{ item }">
              <template v-if="item.RefundPayment">
                <div
                  :class="['sm-banner-alert', statusColorStyle(item.RefundPayment)]"
                  role="alert"
                  class="text-nowrap"
                  style="display: inline; font-size: 11px;"
                >
                  {{ item.RefundPayment }}
                </div>
              </template>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for refund payment -->

            <!-- #region::Virtual column for discount to commission -->
            <template #cell(CommissionDiscount)="{ item }">
              <template v-if="DISCOUNT_TO_COMMISSION_STATUS.includes(item.CommissionDiscount)">
                <div
                  :class="['sm-banner-alert', statusColorStyle(item.CommissionDiscount)]"
                  role="alert"
                  class="text-nowrap ml-0"
                  style="display: inline; font-size: 11px;"
                >
                  {{ item.CommissionDiscount }}
                </div>
              </template>
              <span
                v-else
                class="text-nowrap"
              >Por definir</span>
            </template>
            <!-- #endregion::Virtual column for discount to commission -->

          </b-table>

          <!-- #region::Alert when no transfers are available -->
          <b-alert
            v-if="!availableRefunds && !isLoadingRefunds"
            variant="warning"
            class="my-0"
            show
          >
            <div class="alert-body">
              <span>No se encontraron devoluciones.</span>
            </div>
          </b-alert>
          <!-- #endregion::Alert when no transfers are available -->

        </b-card-text>
      </b-card>
    </template>

    <!-- #region begin::Pagination & items per list -->
    <BasicPaginator
      v-if="availableRefunds"
      ref="basic-paginator"
      class="mt-2"
      :callback="handleChangePagination"
      :total-rows="totalRefunds"
    />
    <!-- #endregion end::Pagination & items per list -->

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  BSkeleton, BCard, BCardText, BTable, BAlert,
} from 'bootstrap-vue'

import BasicPaginator from '@/components/tables/BasicPaginator.vue'
import RefundsListFilters from '@/modules/store/refunds/components/RefundsListFilters.vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getError from '@/helpers/ErrorsHandler'

const DISCOUNT_TO_COMMISSION_STATUS = ['Pendiente', 'Por aplicar', 'Aplicado']

export default {
  components: {
    BCard,
    BCardText,
    BTable,
    BAlert,
    BSkeleton,
    BasicPaginator,
    RefundsListFilters,
  },
  data() {
    return {
      lastPage: 0,
      indexFrom: 0,
      totalRefunds: 0,
      /**
       * Columnas para la lista de devoluciones
       */
      columns: [
        {
          key: 'No',
          label: 'No.',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'ID',
          label: 'ID',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'Qty',
          label: 'Productos',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'OrderID',
          label: 'ID Orden',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'Location',
          label: 'Ubicación',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
          tdClass: 'text-nowrap',
        },
        {
          key: 'TotalMxn',
          label: 'Total',
          thClass: !this.$ability.can('received', 'Devolution') ? 'bg-light' : 'd-none',
          tdClass: !this.$ability.can('received', 'Devolution') ? '' : 'd-none',
          thStyle: 'text-transform: none;',
        },
        {
          key: 'status',
          label: 'Estatus',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'received_month',
          label: 'Mes recibida',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'received_date',
          label: 'Fecha de recibida',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'RefundPayment',
          label: 'Pago reembolso',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'CommissionDiscount',
          label: 'Descuento a comisión',
          thClass: this.$ability.can('read status_commission', 'Devolution') ? 'bg-light' : 'd-none',
          tdClass: this.$ability.can('read status_commission', 'Devolution') ? '' : 'd-none',
          thStyle: 'text-transform: none;',
        },
        {
          key: 'Creator',
          label: 'Creador',
          thClass: this.$ability.can('read creator', 'Devolution') ? 'bg-light' : 'd-none',
          tdClass: this.$ability.can('read creator', 'Devolution') ? '' : 'd-none',
          thStyle: 'text-transform: none;',
        },
        {
          key: 'created_at',
          label: 'Creada',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
      ],
      /**
       * Registros de devoluciones
       */
      refunds: [],
      isLoadingRefunds: false,

      DISCOUNT_TO_COMMISSION_STATUS,
    }
  },
  computed: {
    ...mapGetters({
      /**
       * Filtros globales
       */
      getFilteredSortBy: 'filters/getFilteredSortBy',
      getFilteredStatus: 'filters/getFilteredStatus',
      getFilteredCreator: 'filters/getFilteredCreator',
      getFilteredPerPage: 'filters/getFilteredPerPage',
      getAreLoadedFilters: 'filters/getAreLoadedFilters',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredCurrentPage: 'filters/getFilteredCurrentPage',
      getFilteredReceivedDate: 'filters/getFilteredReceivedDate',
      getFilteredCreatedAtDate: 'filters/getFilteredCreatedAtDate',
      getFilteredReceivedMonth: 'filters/getFilteredReceivedMonth',
      getFilteredPiecesLocation: 'filters/getFilteredPiecesLocation',
      getFilteredCommissionStatus: 'filters/getFilteredCommissionStatus',
      getFilteredRefundPaymentStatus: 'filters/getFilteredRefundPaymentStatus',
    }),
    /**
     * Ordenamiento global (filtro en store)
     */
    filteredSortBy: {
      get() { return this.getFilteredSortBy },
      set(value) { this.setFilteredSortBy(value) },
    },
    /**
     * Filtro de estatus (filtro en store)
     */
    filteredStatus: {
      get() { return this.getFilteredStatus },
      set(value) { this.setFilteredStatus(value) },
    },
    /**
     * Filtro de creador (filtro en store)
     */
    filteredCreator: {
      get() { return this.getFilteredCreator },
      set(value) { this.setFilteredCreator(value) },
    },
    /**
     * Indica la cantidad de elementos por página (paginado global)
     */
    filteredPerPage: {
      get() { return this.getFilteredPerPage },
      set(value) { this.setFilteredPerPage(value) },
    },
    /**
     * Indica si los filtros globales ya fueron cargados (paginado global)
     */
    areLoadedFilters: {
      get() { return this.getAreLoadedFilters },
    },
    /**
     * Término de búsqueda filtrado (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
      set(value) { this.setFilteredSearchTerm(value) },
    },
    /**
     * Indica la página actual (paginado global)
     */
    filteredCurrentPage: {
      get() { return this.getFilteredCurrentPage },
      set(value) { this.setFilteredCurrentPage(value) },
    },
    /**
     * Filtro de fecha de recepción (filtro en store)
     */
    filteredReceivedDate: {
      get() { return this.getFilteredReceivedDate },
      set(value) { this.setFilteredReceivedDate(value) },
    },
    /**
     * Filtro de fecha de creación (filtro en store)
     */
    filteredCreatedAtDate: {
      get() { return this.getFilteredCreatedAtDate },
      set(value) { this.setFilteredCreatedAtDate(value) },
    },
    /**
     * Filtro de mes de recepción (filtro en store)
     */
    filteredReceivedMonth: {
      get() { return this.getFilteredReceivedMonth },
      set(value) { this.setFilteredReceivedMonth(value) },
    },
    /**
     * Filtro de ubicación de piezas (filtro en store)
     */
    filteredPiecesLocation: {
      get() { return this.getFilteredPiecesLocation },
      set(value) { this.setFilteredPiecesLocation(value) },
    },
    /**
     * Filtro de estatus de descuento a comisión (filtro en store)
     */
    filteredCommissionStatus: {
      get() { return this.getFilteredCommissionStatus },
      set(value) { this.setFilteredCommissionStatus(value) },
    },
    /**
     * Filtro de estatus de pago de reembolso (filtro en store)
     */
    filteredRefundPaymentStatus: {
      get() { return this.getFilteredRefundPaymentStatus },
      set(value) { this.setFilteredRefundPaymentStatus(value) },
    },
    availableRefunds() {
      return this.totalRefunds > 0
    },
  },

  /**
  * Hook que se ejecuta cuando el componente es creado
  *
  * @summary Verifica si hay parámetros en la ruta provenientes del dashboard
  * y carga la lista de devoluciones
  */
  async created() {
    // Verify if are params in route from dashboard
    if (this.$store.state.dashboard.paramsDashboard) {
      this.filteredStatus = this.$store.state.dashboard.paramsDashboard
      this.listenerParmasDashboard('')
    }

    this.loadRefundsListWithVerification()
  },
  methods: {
    ...mapActions({
      loadRefunds: 'refunds/loadRefunds',
      loadCreatorsSuggests: 'refunds/loadCreatorsSuggests',
      listenerParmasDashboard: 'dashboard/listenerParmasDashboard',

      /**
       * Filtros globales
       */
      setFilteredSortBy: 'filters/setFilteredSortBy',
      setFilteredStatus: 'filters/setFilteredStatus',
      setFilteredCreator: 'filters/setFilteredCreator',
      setFilteredPerPage: 'filters/setFilteredPerPage',
      setFilteredSearchTerm: 'filters/setFilteredSearchTerm',
      setFilteredCurrentPage: 'filters/setFilteredCurrentPage',
      setFilteredReceivedDate: 'filters/setFilteredReceivedDate',
      setFilteredCreatedAtDate: 'filters/setFilteredCreatedAtDate',
      setFilteredReceivedMonth: 'filters/setFilteredReceivedMonth',
      setFilteredPiecesLocation: 'filters/setFilteredPiecesLocation',
      setFilteredCommissionStatus: 'filters/setFilteredCommissionStatus',
      setFilteredRefundPaymentStatus: 'filters/setFilteredRefundPaymentStatus',
    }),

    /**
    * Evento de filtrado
    *
    * @summary Evento del componente de filtrado. Devuelve los registros en base
    * al tipo de filtro y recarga la lista de devoluciones
    * @param filter - Objeto con el tipo y valor del filtro
    */
    async handleFilter(filter) {
      switch (filter.type) {
        case 'creator': this.filteredCreator = filter.value
          break
        case 'date': this.filteredCreatedAtDate = filter.value
          break
        case 'status': this.filteredStatus = filter.value
          break
        case 'location': this.filteredPiecesLocation = filter.value
          break
        case 'refund-payment-status': this.filteredRefundPaymentStatus = filter.value
          break
        case 'sort': this.filteredSortBy = filter.value
          break
        case 'received-date': this.filteredReceivedDate = filter.value
          break
        case 'received-month': this.filteredReceivedMonth = filter.value
          break
        case 'commission-status': this.filteredCommissionStatus = filter.value
          break
        default: this.filteredSearchTerm = filter.value
          this.filteredCurrentPage = 1
          // eslint-disable-next-line no-unused-expressions
          this.$refs['basic-paginator']?.resetCurrentPage()
          break
      }

      this.loadRefundsListWithVerification()
    },

    /**
    * Carga de elementos de la lista con verificación de paginado
    *
    * @summary Carga la lista de elementos y verifica que la página actual contenga elementos
    */
    async loadRefundsListWithVerification() {
      await this.loadRefundsList()
      this.verifyContenList()
    },

    /**
    * Evento de paginación
    *
    * @summary Evento del componente de paginación. Establece la página actual y la cantidad
    * de registros por página.
    * @param {number} currentPage - Página actual.
    * @param {number} perPage - Elementos a mostrar por página.
    */
    async handleChangePagination(currentPage, perPage) {
      this.filteredCurrentPage = currentPage
      this.filteredPerPage = perPage
      this.loadRefundsList()
    },

    /**
    * Verificación de contenido
    *
    * @summary Verifica que la página actual tenga registros, en caso de no tenerlos
    * tomará la última página disponible y cargará los registros
    */
    async verifyContenList() {
      if (this.refunds.length === 0) {
        this.filteredCurrentPage = this.lastPage

        // Verifica si hay registros disponibles en el servidor
        if (this.availableRefunds) {
          this.$refs['basic-paginator'].resetCurrentPage(this.lastPage)
          await this.loadRefundsList()
        }
      }
    },

    /**
    * Carga de registros de tipo traspasos
    *
    * @summary ⁡⁢⁣⁡⁢⁢⁡⁣⁣⁡⁣⁢⁣⁡⁣⁢⁢⁡⁢⁢⁢‍Carga y establece los registro de traspasos y los parámetros de paginación⁡⁡⁡⁡⁡⁡
    * @exception ⁡⁢⁣⁢Error de validación en la carga de registro de traspasos⁡
    */
    async loadRefundsList() {
      try {
        this.$swal({
          title: 'Cargando...',
          allowOutsideClick: false,
        })
        this.$swal.showLoading()
        this.isLoadingRefunds = true

        const response = await this.loadRefunds(this.loadParams())
        this.refunds = response.data.data.data
        this.totalRefunds = response.data.data.total
        this.indexFrom = response.data.data.from
        this.lastPage = response.data.data.last_page

        if (this.$ability.can('read creator', 'Devolution')) {
          await this.loadCreatorsSuggests()
        }

        this.isLoadingRefunds = false
        this.$swal.close()
      } catch (error) {
        this.$swal.close()
        this.showToast('Error de validación', getError(error), 'danger')
      }
    },

    /**
    * Parámetros de carga
    *
    * @summary Devuelve los parámetros de carga de la lista
    */
    loadParams() {
      return {
        perPage: this.filteredPerPage,
        dateTerm: this.filteredCreatedAtDate,
        searchTerm: this.filteredSearchTerm,
        sortTerm: this.filteredSortBy,
        statusTerm: this.filteredStatus,
        currentPage: this.filteredCurrentPage,
        creatorTerm: this.filteredCreator.id,
        locationTerm: this.filteredPiecesLocation,
        receivedDateTerm: this.filteredReceivedDate,
        receivedMonthTerm: this.filteredReceivedMonth,
        commissionStatusTerm: this.filteredCommissionStatus,
        refundPaymentStatusTerm: this.filteredRefundPaymentStatus,
      }
    },

    /**
    * Color del tag de estatus
    *
    * @summary Define el color que debe aplicarse a los tags de estatus
    * @param {string} status - Estatus de la devolución
    */
    statusColorStyle(status) {
      switch (status) {
        case 'Solicitud':
          return 'alert-purple'
        case 'Aprobada':
        case 'Pagado':
        case 'Aplicado':
          return 'alert-green'
        case 'Rechazada':
          return 'alert-orange'
        case 'Cancelada':
          return 'alert-gray'
        case 'Pendiente':
          return 'alert-cyan'
        case 'Por aplicar':
          return 'alert-yellow'
        default:
          return 'alert-blue'
      }
    },

    /**
    * Muestra un mensaje de tipo toast
    *
    * @param {string} title - Título del mensaje
    * @param {string} text - Contenido del mensaje
    * @param {string} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
